import { type FinishReason, type LanguageModelUsage } from 'ai'
import type { IMcpToolRegistry, McpToolAttachment, McpToolUser } from './IMcpTool'
import type { AiChatPageContext } from './AiChatPageContext'
import type { ITracerService } from '../telemetry'
import type { LanguageModelOverrideArgs } from './args/LanguageModelOverrideArgs'

export enum OutputFormatEnum {
  MARKDOWN = 'markdown',
  HTML = 'html',
  TEXT = 'text',
  RAW = 'raw',
}

/**
 * Stored prompt contexts — fixed set of AI-prompt use cases whose system
 * prompts are persisted in `AppSettings` and can be customized per tenant
 * via the Admin UI. Each enum value is the wire-stable identifier used in:
 * - `StoredPromptConfigs` (Record key in libs/ai/common/constants.ts)
 * - `classifyIntent` allowlists (runtime strings passed to the classifier)
 * - Admin UI prompt-config pages
 *
 * The string values MUST stay byte-identical to the pre-enum union because
 * they are consumed as `Record<StoredPromptContextEnum, T>` keys and are
 * threaded through runtime classification allowlists. Never renumber or
 * rename a value without a coordinated migration.
 */
export enum StoredPromptContextEnum {
  CreateOrder = 'create-order',
  GenerateProductDescription = 'generate-product-description',
  ExtractAddress = 'extract-address',
  GenerateProductName = 'generate-product-name',
  GenerateBlogTags = 'generate-blog-tags',
  GenerateBlogSummary = 'generate-blog-summary',
  SummarizeTranscription = 'summarize-transcription',
  ExtractDocumentCode = 'extract-document-code',
  FormalizeCommunication = 'formalize-communication',
  StorefrontSearch = 'storefront-search',
  CreatePurchaseOrder = 'create-purchase-order',
  CreateProduct = 'create-product',
  HaulerAudioPickupExtraction = 'hauler-audio-pickup-extraction',
  HaulerManifestClassification = 'hauler-manifest-classification',
  HaulerManifestExtractionLanco = 'hauler-manifest-extraction-lanco',
  HaulerManifestExtractionMvmpca = 'hauler-manifest-extraction-mvmpca',
  HaulerManifestExtractionUmwo30 = 'hauler-manifest-extraction-umwo30',
}

/**
 * MIME types for supported attachment formats
 */
export type AttachmentMimeType = 'application/pdf' | 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp'

/** Token totals of one chat turn, summed across every finished step. */
export type ChatTurnUsage = { inputTokens: number; outputTokens: number }

/**
 * Options for streaming chat via StreamChat action
 */
export interface StreamChatOptions extends LanguageModelOverrideArgs {
  /** Tool registry to create tools from */
  toolRegistry?: IMcpToolRegistry
  /** Optional PDF/image URL for vision analysis */
  attachmentUrl?: string
  /**
   * MIME type of the attachment.
   * Required when attachmentUrl is provided to select the appropriate prompt.
   * Supported types: 'application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'
   */
  attachmentMimetype?: AttachmentMimeType
  /**
   * Original filename of the attachment (e.g., 'LYAL.pdf').
   * Optional. When set, downstream MCP tools use the filename stem as
   * the asset title in the PO/Order's Assets list. When unset, the tool
   * falls back to a generic default title.
   */
  attachmentFilename?: string
  /** Maximum number of tool call steps (default: 5) */
  maxSteps?: number
  /**
   * Callback when streaming finishes. `usage` is the final step's usage;
   * `totalUsage` sums every step of the turn and `recordedModel` names the
   * model that actually ran.
   */
  onFinish?: (result: {
    finishReason: FinishReason
    usage: LanguageModelUsage
    totalUsage: LanguageModelUsage
    recordedModel: string
  }) => void | Promise<void>
  /**
   * Called when `abortSignal` aborts the stream, with the usage summed over the
   * steps that finished before the abort. Never called without an `abortSignal`.
   */
  onAbort?: (result: { usage: ChatTurnUsage; recordedModel: string }) => void | Promise<void>
  /**
   * Aborts the model stream when it fires. Omitted → the stream runs to
   * completion regardless of the client connection.
   */
  abortSignal?: AbortSignal
  /**
   * Login token every tool GraphQL call runs under for this request (the
   * caller's own `api` token). Omitted → the App Service User's token.
   */
  loginToken?: string
  /**
   * Pre-resolved system prompt override.
   *
   * When set (non-undefined), `StreamChat` uses this value directly instead
   * of running the data-driven intent classifier (allowlist derived from
   * `StoredPromptConfigs[*].classifiable`) or falling back to the default
   * chat prompt. Use this when the caller has already resolved the system
   * prompt out-of-band (e.g. a per-surface router calling `classifyIntent()`
   * with a custom context allowlist).
   */
  systemPromptOverride?: string
  /**
   * Per-step callback forwarded directly to `streamText({ onStepFinish })`.
   *
   * Typed as `any` here because the AI SDK's `StepResult<ToolSet>` is a deeply
   * generic type we deliberately do not surface into `@ztechapps/common.contract`
   * (to keep the contract package free of the `ai` dependency). Concrete call
   * sites cast to the SDK type at the boundary.
   */
  onStepFinish?: (step: any) => void | Promise<void>
  /**
   * User context for tool execution audit logging.
   * Required for proper audit trails. If not provided, tools will log as 'anonymous'.
   */
  userContext?: AiToolUserContext
  /**
   * One identifier per chat turn, spanning every streaming step and tool call
   * of that turn. Distinct from the per-tool-call `correlationId`.
   */
  chatTurnId?: string
  /**
   * Page context from the admin chat request (optional).
   * Present when the web app sent its open page along with the message.
   * Forwarded to the assembled tools' execution context.
   */
  pageContext?: AiChatPageContext
  /**
   * Text appended to the resolved system prompt (optional).
   * Present when the caller rendered the page context into a context block.
   * When absent, the model call is unchanged.
   */
  contextBlock?: string
}

/**
 * User context for AI chat tool execution.
 * Passed from the authenticated request to ensure proper audit logging.
 */
export interface AiToolUserContext {
  /** The authenticated user's ID */
  userId: string
  /** Optional tenant ID for multi-tenant applications */
  tenantId?: string
  /**
   * The signed-in caller, forwarded to each tool's `McpToolExecutionContext.user`.
   * Never carries `auth` or any token.
   */
  user?: McpToolUser
}

/**
 * Options for creating AI tools from registry
 */
export interface CreateAiToolsOptions {
  /** User context from authenticated request */
  userContext: AiToolUserContext
  /**
   * The `api`-scoped login token every tool GraphQL call runs under for this
   * request — the caller's own token when `StreamChatOptions.loginToken` is
   * set, otherwise the App Service User's (StreamChat resolves it via
   * `appUserService.getAccessToken()` before assembling tools).
   * Threaded into each tool's `McpToolExecutionContext.loginToken`.
   */
  loginToken: string
  /** Tracer service for audit logging */
  tracerService?: ITracerService
  /**
   * Attachment metadata to forward to tool execution context (optional).
   * When provided, each tool will receive this in their McpToolExecutionContext.
   */
  attachment?: McpToolAttachment
  /**
   * Page context to forward to tool execution context (optional).
   * When provided, each tool will receive this in their McpToolExecutionContext.
   */
  pageContext?: AiChatPageContext
  /**
   * Tenant id used to resolve per-tool description augmentations via
   * `IMcpTool.getDescriptionAugmentation`. When omitted, augmentations are
   * skipped entirely — tools emit their static definition only. The admin
   * `StreamChat` path currently leaves this undefined (the call site is
   * tenant-agnostic today), which preserves byte-identical behaviour for the
   * PDF-to-order flow.
   */
  tenantId?: string
  /**
   * Identifier of the chat turn the assembled tools run under, threaded into
   * each tool's execution context so a tool's own log line joins to its turn.
   * When omitted, tool assembly behaves exactly as before the field existed.
   */
  chatTurnId?: string
  /**
   * Pre-resolved description augmentation string to append to the tool's
   * static `description`. Used internally by `createAiToolsFromRegistry` to
   * pass the awaited `getDescriptionAugmentation` output into the synchronous
   * `createAiToolFromMcp` adapter without forcing callers onto an async API.
   * Direct callers of `createAiToolFromMcp` may also supply this to inject
   * a custom augmentation.
   */
  descriptionAugmentation?: string
}
